import { HttpRange } from '@core/http';
import { Filter } from '@shared/modules/filter';
import debouncePromise from 'debounce-promise';
import * as EI from 'fp-ts/Either';
import { pipe } from 'fp-ts/function';
import * as T from 'fp-ts/Task';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DEFAULT_RANGE_SIZE, defaultRangeCursor, Range, RangeCursor } from './model';

export function useRange<R, F extends Filter = {}>(
  task: (cursor: RangeCursor, filter?: F, sort?: string) => HttpRange<R, F>,
  defaultFilter: F,
  hideError?: boolean,
) {
  const [range, setRange] = useState<Range<R, F>>(() => Range.default());
  const [filter, setFilter] = useState<F>(() => defaultFilter);

  const f = useRef<F>(defaultFilter);

  const fetchRange = useCallback(
    (cursor: RangeCursor = {}, filter?: F, disableMerge?: boolean) =>
      pipe(
        task(cursor, filter),
        T.chainIOK(result =>
          pipe(
            result,
            EI.fold(
              () => () => {
                if (!hideError) {
                  toast.error('Une erreur technique est survenue');
                }
                setRange(old => old.setLoading(false));
              },
              range => () => setRange(old => (disableMerge ? range : old.merge(range))),
            ),
          ),
        ),
      )(),
    [task, hideError],
  );

  const debouncedFetchRange = useMemo(() => debouncePromise(fetchRange, 150), [fetchRange]);

  const handleLoadMore = useCallback(
    (cursor: RangeCursor) => debouncedFetchRange(cursor, f.current),
    [debouncedFetchRange],
  );

  const handleRefreshIndex = useCallback(
    (index: number) => {
      const startIndex = Math.max(0, index - DEFAULT_RANGE_SIZE / 2);

      return fetchRange(
        {
          startIndex,
          endIndex: startIndex + DEFAULT_RANGE_SIZE - 1,
        },
        f.current,
        true,
      );
    },
    [fetchRange],
  );

  const handleFilter = useCallback(
    (filter: F) => {
      setFilter(filter);

      f.current = filter;

      setRange(old => old.setLoading());

      return fetchRange(defaultRangeCursor, filter);
    },
    [fetchRange],
  );

  return {
    range,
    filter,
    defaultFilter,
    handleLoadMore,
    handleFilter,
    handleRefreshIndex,
    setRange,
  };
}
